import {
  Button,
  Card,
  CardBody,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
} from "@heroui/react";
import {
  AlertOctagon,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  MoreHorizontal,
  Package,
  Search,
} from "lucide-react";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useCustomerOrdersV2,
  getOrderStatus,
  isOrderAwaitingPayment,
  getRecipientFromOrder,
} from "@/api/use-customer-orders-v2";
import { ShipmentStatusBadge } from "@/components/shipment-status-badge";
import { type Order, ShipmentStatus } from "@/api.types.v2";
import { PayOrderButton } from "./pay-order-button";
import { useRequestCancellation } from "@/api/use-request-cancellation";
import { toast } from "@/hooks/use-toast";
import { PrintShipmentLabelButton } from "./components/print-shipment-label-button";
import { OrderDetailsDialog } from "./components/order-details-dialog";
import { UpcomingShipments } from "./upcoming-shipments";
import { useProofMediaPreconnect } from "./use-proof-media-preconnect";

import { canRequestCustomerShipmentChanges } from "@/api/shipment-action-eligibility";
import { ShipmentChangeRequestDialog } from "./shipment-change-request-dialog";
import { OrderPaymentSummary } from "./order-payment-summary";

function RowMoreActions({
  onDetails,
  onCancel,
  onChanges,
}: {
  onDetails: () => void;
  onCancel?: () => void;
  onChanges?: () => void;
}) {
  const { t } = useTranslation("app");
  return (
    <Dropdown placement="bottom-end" disableAnimation>
      <DropdownTrigger>
        <Button
          isIconOnly
          size="sm"
          variant="light"
          aria-label={t("orders.moreActions")}
          className="h-8 min-w-8 w-8 text-default-500"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={t("orders.moreActions")}
        onAction={(key) => {
          if (key === "details") onDetails();
          else if (key === "cancel") onCancel?.();
          else if (key === "changes") onChanges?.();
        }}
      >
        <DropdownItem key="details">{t("orders.details")}</DropdownItem>
        {onChanges ? (
          <DropdownItem
            key="changes"
            color="warning"
            className="text-warning-700"
          >
            {t("orders.requestChanges")}
          </DropdownItem>
        ) : null}
        {onCancel ? (
          <DropdownItem key="cancel" color="danger" className="text-danger">
            {t("orders.requestCancellation")}
          </DropdownItem>
        ) : null}
      </DropdownMenu>
    </Dropdown>
  );
}

function formatDate(dateString: string, locale = "en-CA"): string {
  return new Date(dateString).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const CANCELLABLE_STATUSES: ShipmentStatus[] = [
  ShipmentStatus.PENDING_PAYMENT,
  ShipmentStatus.LABEL_READY,
];

function canCancelOrder(status: ShipmentStatus | null): boolean {
  if (!status) return false;
  return CANCELLABLE_STATUSES.includes(status);
}

type StatusFilter =
  | "all"
  | "in_transit"
  | "label_ready"
  | "delivered"
  | "issues";

const STATUS_FILTERS: { key: StatusFilter }[] = [
  { key: "all" },
  { key: "in_transit" },
  { key: "label_ready" },
  { key: "delivered" },
  { key: "issues" },
];

const IN_TRANSIT_SET = new Set<ShipmentStatus>([
  ShipmentStatus.PICKED_UP,
  ShipmentStatus.IN_TRANSIT,
  ShipmentStatus.AT_FACILITY,
  ShipmentStatus.OUT_FOR_DELIVERY,
]);

function hasIssue(order: Order): boolean {
  const status = getOrderStatus(order);
  return (
    status === ShipmentStatus.EXCEPTION ||
    status === ShipmentStatus.DELIVERY_ATTEMPTED ||
    status === ShipmentStatus.RETURNED
  );
}

function isCancellationPending(order: Order): boolean {
  return (
    order.shipments?.some((s) => s.cancellationRequestedAt != null) ?? false
  );
}

function matchesStatusFilter(order: Order, filter: StatusFilter): boolean {
  if (filter === "all") return true;
  const status = getOrderStatus(order);
  if (filter === "issues") {
    return (
      isCancellationPending(order) ||
      hasIssue(order) ||
      status === ShipmentStatus.CANCELLED
    );
  }
  if (filter === "in_transit")
    return status ? IN_TRANSIT_SET.has(status) : false;
  if (filter === "label_ready") return status === ShipmentStatus.LABEL_READY;
  if (filter === "delivered") return status === ShipmentStatus.DELIVERED;
  return true;
}

interface ShipmentRowProps {
  order: Order;
  isFirst: boolean;
  isLast: boolean;
}

function ShipmentRow({ order, isFirst, isLast }: ShipmentRowProps) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const status = getOrderStatus(order);
  const recipient = getRecipientFromOrder(order);
  const shipment = order.shipments?.[0];
  const packageCount = shipment?.packages?.length ?? 0;
  const labelUrl = shipment?.packages?.find((p) => p.labelUrl)?.labelUrl;
  const shipmentNumberLabel = shipment?.shipmentNumber
    ? `SHP-${shipment.shipmentNumber}`
    : t("orders.shipmentNumberUnavailable");

  const requestCancellation = useRequestCancellation({
    onSuccess: (data) => {
      toast({
        title: t("orders.toastCancellationTitle"),
        description: data.message,
      });
      setCancelDialogOpen(false);
    },
    onError: () =>
      toast({
        variant: "destructive",
        title: t("orders.toastCancellationError"),
      }),
  });

  const cancellationPending = isCancellationPending(order);
  const issueDetected = hasIssue(order);

  const showPay = isOrderAwaitingPayment(order);
  const showPrint =
    status === ShipmentStatus.LABEL_READY && !cancellationPending;
  const showCancel = canCancelOrder(status) && !cancellationPending;
  const showChanges =
    canRequestCustomerShipmentChanges(shipment?.status) && !cancellationPending;

  const rail = issueDetected
    ? "border-l-danger-500 bg-danger-50/40"
    : cancellationPending
      ? "border-l-warning-500 bg-warning-50/40"
      : "border-l-transparent";

  return (
    <>
      <div
        className={`border-b border-divider last:border-b-0 ${
          isFirst ? "" : ""
        } ${isLast ? "" : ""} ${rail} border-l-[3px]`}
      >
        {issueDetected && (
          <InlineBanner
            tone="danger"
            title={t("orders.issueTitle")}
            body={t("orders.issueBody")}
          />
        )}
        {cancellationPending && !issueDetected && (
          <InlineBanner
            tone="warning"
            title={t("orders.cancellationPendingTitle")}
            body={t("orders.cancellationPendingBody")}
          />
        )}

        {/* Mobile row — stacked card */}
        <div
          className="md:hidden px-4 py-3.5 cursor-pointer hover:bg-default-50 transition-colors space-y-1.5"
          onClick={() => setDetailsOpen(true)}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono font-semibold text-default-900 text-sm whitespace-nowrap">
              {shipmentNumberLabel}
            </div>
            <ShipmentStatusBadge
              status={status}
              paymentStatus={order.paymentStatus}
            />
          </div>
          <div className="text-default-700 text-sm truncate">
            {recipient?.name ?? "—"}
          </div>
          <div className="flex items-center gap-2 text-tiny text-default-500">
            <span className="inline-flex items-center gap-1">
              <Package className="h-3.5 w-3.5" />
              {packageCount}
            </span>
            <span className="text-default-300">·</span>
            <span>{formatDate(order.createdAt, locale)}</span>
          </div>
          <div
            className="flex items-center gap-2 pt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <OrderPaymentSummary order={order} />
            <div className="flex items-center gap-1 ml-auto">
              {showPay ? (
                <PayOrderButton order={order} />
              ) : showPrint ? (
                <PrintShipmentLabelButton
                  shipmentId={shipment?.id}
                  disabled={!labelUrl}
                  label={t("orders.print")}
                  disabledTitle={t("orders.labelNotReady")}
                />
              ) : (
                <Button
                  size="sm"
                  variant="light"
                  onPress={() => setDetailsOpen(true)}
                >
                  {t("orders.details")}
                </Button>
              )}
              {(showCancel || showChanges) && (
                <RowMoreActions
                  onDetails={() => setDetailsOpen(true)}
                  onCancel={
                    showCancel ? () => setCancelDialogOpen(true) : undefined
                  }
                  onChanges={
                    showChanges ? () => setChangeOpen(true) : undefined
                  }
                />
              )}
            </div>
          </div>
        </div>

        {/* Desktop row — wide grid */}
        <div
          className="hidden md:grid grid-cols-[120px_110px_minmax(0,1fr)_40px_minmax(0,220px)_120px] gap-4 items-center px-5 py-3.5 text-sm cursor-pointer hover:bg-default-50 transition-colors"
          onClick={() => setDetailsOpen(true)}
        >
          <div>
            <div className="font-mono font-semibold text-default-900">
              {shipmentNumberLabel}
            </div>
            <div className="text-tiny text-default-500 mt-0.5 whitespace-nowrap">
              {formatDate(order.createdAt, locale)}
            </div>
          </div>

          <div>
            <ShipmentStatusBadge
              status={status}
              paymentStatus={order.paymentStatus}
            />
          </div>

          <div className="text-default-700 truncate">
            {recipient?.name ?? "—"}
          </div>

          <div className="inline-flex items-center gap-1.5 text-default-600">
            <Package className="h-3.5 w-3.5" />
            {packageCount}
          </div>

          <div className="min-w-0">
            <div className="flex min-w-0 flex-col items-start gap-2">
              <OrderPaymentSummary order={order} />
              {shipment?.trackingNumber && (
                <a
                  href={`/tracking/${shipment.trackingNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline inline-flex max-w-full items-start gap-1 font-mono text-tiny"
                  title={shipment.trackingNumber}
                >
                  <span className="min-w-0 break-all">
                    {shipment.trackingNumber}
                  </span>
                  <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0" />
                </a>
              )}
            </div>
          </div>

          <div
            className="flex items-center justify-end gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {showPay ? (
              <PayOrderButton order={order} />
            ) : showPrint ? (
              <PrintShipmentLabelButton
                shipmentId={shipment?.id}
                disabled={!labelUrl}
                label={t("orders.print")}
                disabledTitle={t("orders.labelNotReady")}
              />
            ) : (
              <Button
                size="sm"
                variant="light"
                onPress={() => setDetailsOpen(true)}
              >
                {t("orders.view")}
              </Button>
            )}
            {(showCancel || showChanges) && (
              <RowMoreActions
                onDetails={() => setDetailsOpen(true)}
                onCancel={
                  showCancel ? () => setCancelDialogOpen(true) : undefined
                }
                onChanges={showChanges ? () => setChangeOpen(true) : undefined}
              />
            )}
          </div>
        </div>
      </div>

      <OrderDetailsDialog
        order={order}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />

      {shipment && (
        <ShipmentChangeRequestDialog
          shipment={shipment}
          open={changeOpen}
          onOpenChange={setChangeOpen}
        />
      )}

      <Modal
        isOpen={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{t("orders.cancelModalTitle")}</ModalHeader>
              <ModalBody>
                <p className="text-sm text-default-600">
                  {t("orders.cancelModalBody")}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t("orders.keepOrder")}
                </Button>
                <Button
                  color="danger"
                  onPress={() =>
                    requestCancellation.mutate({ orderId: order.id })
                  }
                  isDisabled={requestCancellation.isPending}
                  isLoading={requestCancellation.isPending}
                >
                  <span>{t("orders.requestCancellation")}</span>
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

function InlineBanner({
  tone,
  title,
  body,
}: {
  tone: "warning" | "danger";
  title: string;
  body: string;
}) {
  const Icon = tone === "danger" ? AlertOctagon : Clock;
  const titleColor = tone === "danger" ? "text-danger-700" : "text-warning-700";
  const bodyColor =
    tone === "danger" ? "text-danger-700/85" : "text-warning-700/85";
  const iconColor = tone === "danger" ? "text-danger-500" : "text-warning-500";
  return (
    <div className="flex items-start sm:items-center gap-2 px-4 md:px-5 py-2 text-xs">
      <Icon
        className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 sm:mt-0 ${iconColor}`}
      />
      <div className="flex flex-col sm:flex-row sm:gap-2 min-w-0">
        <span className={`font-semibold whitespace-nowrap ${titleColor}`}>
          {title}
        </span>
        <span className={bodyColor}>{body}</span>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="bg-content1 rounded-large shadow-small overflow-hidden">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="border-b border-divider last:border-b-0 px-4 md:px-5 py-3.5"
        >
          {/* Mobile skeleton */}
          <div className="md:hidden space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-2/3 rounded" />
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-3 w-24 rounded" />
              <Skeleton className="h-4 w-16 rounded" />
            </div>
          </div>
          {/* Desktop skeleton */}
          <div className="hidden md:grid grid-cols-[120px_110px_minmax(0,1fr)_40px_minmax(0,220px)_120px] gap-4 items-center">
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-16 rounded" />
              <Skeleton className="h-3 w-20 rounded" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-4 w-32 rounded" />
            <Skeleton className="h-4 w-6 rounded" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-32 rounded" />
              <Skeleton className="h-3 w-16 rounded" />
            </div>
            <div className="flex justify-end">
              <Skeleton className="h-8 w-16 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function OrdersView() {
  const { t } = useTranslation("app");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    data: response,
    isLoading,
    error,
  } = useCustomerOrdersV2({ page, limit });

  useProofMediaPreconnect(response?.data);

  const filteredOrders = useMemo(() => {
    const orders = response?.data ?? [];
    const search = searchTerm.trim().toLowerCase();

    return orders.filter((order) => {
      if (!matchesStatusFilter(order, statusFilter)) return false;
      if (!search) return true;
      const orderNumber = String(order.orderNumber || "");
      const trackingNumber = order.shipments?.[0]?.trackingNumber ?? "";
      const recipient = getRecipientFromOrder(order);

      return (
        orderNumber.includes(search) ||
        trackingNumber.toLowerCase().includes(search) ||
        (recipient?.name?.toLowerCase().includes(search) ?? false) ||
        (recipient?.fullAddress?.toLowerCase().includes(search) ?? false)
      );
    });
  }, [response?.data, searchTerm, statusFilter]);

  const totalPages = response?.metadata?.totalPages ?? 1;

  if (error) {
    return (
      <Card shadow="none" className="border border-danger/50 bg-danger/5">
        <CardBody className="py-8 text-center">
          <p className="text-danger font-medium">
            {t("orders.loadErrorTitle")}
          </p>
          <p className="text-sm text-default-500 mt-1">
            {t("orders.loadErrorBody")}
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <UpcomingShipments />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap">
        <Input
          aria-label={t("orders.searchLabel")}
          placeholder={t("orders.searchPlaceholder")}
          variant="bordered"
          value={searchTerm}
          onValueChange={setSearchTerm}
          startContent={<Search className="h-4 w-4 text-default-400" />}
          className="sm:max-w-sm flex-1"
        />

        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_FILTERS.map((f) => (
            <Chip
              key={f.key}
              as="button"
              size="sm"
              variant={statusFilter === f.key ? "solid" : "bordered"}
              color={statusFilter === f.key ? "primary" : "default"}
              onClick={() => setStatusFilter(f.key)}
              className="cursor-pointer"
            >
              {t(`orders.filters.${f.key}`)}
            </Chip>
          ))}
        </div>
      </div>

      {isLoading ? (
        <ListSkeleton />
      ) : filteredOrders.length === 0 ? (
        <Card shadow="none" className="border border-dashed border-default-300">
          <CardBody className="py-16">
            <div className="text-center">
              <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                <Package className="h-6 w-6" />
              </div>
              <div className="mb-2 text-tiny font-semibold uppercase tracking-wider text-default-500">
                {searchTerm
                  ? t("orders.emptyNoMatches")
                  : t("orders.emptyNothingHere")}
              </div>
              <p className="text-2xl font-bold leading-snug tracking-tight">
                {searchTerm ? (
                  <>
                    {t("orders.emptyMatchesPrefix")}{" "}
                    <em className="text-primary">&ldquo;{searchTerm}&rdquo;</em>
                  </>
                ) : (
                  <>
                    {t("orders.emptyFirstShipmentBefore")}{" "}
                    <em className="text-primary">
                      {t("orders.emptyFirstShipmentEmphasis")}
                    </em>{" "}
                    {t("orders.emptyFirstShipmentAfter")}
                  </>
                )}
              </p>
              <p className="mx-auto mt-3 max-w-sm text-sm text-default-500">
                {searchTerm
                  ? t("orders.emptySearchHelp")
                  : t("orders.emptyDefaultHelp")}
              </p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="bg-content1 rounded-large shadow-small overflow-hidden">
          {filteredOrders.map((order, i) => (
            <ShipmentRow
              key={order.id}
              order={order}
              isFirst={i === 0}
              isLast={i === filteredOrders.length - 1}
            />
          ))}
        </div>
      )}

      {!isLoading && response?.metadata && totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-default-500">
            {t("orders.pagination", { page, totalPages })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="bordered"
              size="sm"
              isDisabled={page <= 1}
              onPress={() => setPage(page - 1)}
              startContent={<ChevronLeft className="h-4 w-4" />}
            >
              {t("orders.previous")}
            </Button>
            <Button
              variant="bordered"
              size="sm"
              isDisabled={page >= totalPages}
              onPress={() => setPage(page + 1)}
              endContent={<ChevronRight className="h-4 w-4" />}
            >
              {t("orders.next")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
